import React, { useEffect } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import CustomTable, { Column } from '../components/CustomTable'; // Import the new CustomTable
import moment from 'moment';
import OrderModal from '../components/OrderModal';
import { OrderInterface } from '../types';
import apiClient from '../services/apiClient';
import EmptyState from '../components/EmptyState';
import OrderIcon from '../assets/svgs/OrderIcon';
import ReactGA from 'react-ga4';
import { CreateButton } from '../components/SharedComponents';
import { getCurrency } from '../services/tableHelper';
import { formatHumanReadableDate } from '../utils/dateUtils';
import { useSelector } from 'react-redux';
import StorefrontIcon from '@mui/icons-material/Storefront';
import Tabbed from '../components/Tabbed';
import StorefrontOrders, { StorefrontOrdersRef } from '../components/StorefrontOrders';

const Wrapper = styled.div`
	background-color: white;
	width: 100%;
	min-height: 100vh;
	padding: 30px 40px;
	box-sizing: border-box;
	@media (max-width: 768px) {
		padding: 20px 20px;
	}
`;

const StatusIndicator = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const StatusItem = styled.div<{ color: string }>`
	display: flex;
	align-items: center;
	font-size: 14px;
	color: ${(props) => props.color};
`;

const StatusDot = styled.div<{ color: string }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${(props) => props.color};
	margin-right: 8px;
`;

const CustomerNameContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

export default function Orders() {
	const [orders, setOrders] = React.useState<OrderInterface[]>([]);
	const [isEditPanelVisible, setIsEditPanelVisible] = React.useState<boolean>(false);
	const [selectedOrder, setSelectedOrder] = React.useState<OrderInterface | null>(null);
	const [fetchState, setFetchState] = React.useState<'loading' | 'success' | 'failed'>('loading');
	const storefrontOrdersRef = React.useRef<StorefrontOrdersRef>(null);

	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/orders',
			title: 'Orders',
		});
		setFetchState('loading');
		apiClient
			.get('/orders')
			.then((response: any) => {
				setFetchState('success');
				setOrders(response.data);
				ReactGA.event({
					category: 'Orders',
					action: 'Fetched Orders',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.error('Error fetching orders:', error);
				setFetchState('failed');
				ReactGA.event({
					category: 'Orders',
					action: 'Fetch Orders Failed',
					label: 'Fetch Failed',
				});
			});
	}, []);

	const columns: Column[] = [
		{
			field: 'date_created',
			headerName: 'Date Ordered',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => {
				const date = params.row.backdated_order_date || params.row.date_created;
				return moment(date).format('YYYY-MM-DD');
			},
			valueGetter: (params) => {
				return params.row.backdated_order_date || params.row.date_created;
			},
			flex: 1,
		},
		{
			field: 'customer_name',
			headerName: 'Customer',
			minWidth: 190,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 2,
			renderCell: (params) => {
				const isStorefrontOrder = params.row.source === 'storefront';
				return (
					<CustomerNameContainer>
						{isStorefrontOrder ? (
							<StorefrontIcon
								fontSize="small"
								style={{ color: '#3a5bd9', width: '16px', height: '16px' }}
							/>
						) : (
							<div style={{ width: '16px', height: '16px' }}></div>
						)}
						<span>{params.value}</span>
					</CustomerNameContainer>
				);
			},
		},
		{
			field: 'total_price',
			headerName: 'Price Total',
			minWidth: 120,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			valueGetter: (params) => `${currency} ${Number(params.value).toFixed(2)}`,
			flex: 1,
		},
		{
			field: 'total_quantity',
			headerName: 'Quantity Total',
			minWidth: 120,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			flex: 1,
		},
		{
			field: 'scheduled_date',
			headerName: 'Scheduled For',
			minWidth: 180,
			headerClassName: 'table-header',
			cellClassName: 'text-blue-600',
			renderCell: (params) => {
				if (!params.value) return '-';
				return formatHumanReadableDate(params.value);
			},
			flex: 1,
		},
		{
			field: 'status',
			headerName: 'Status',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			flex: 1,
			renderCell: (params) => {
				const paymentStatus = params.row.is_order_paid
					? 'Paid'
					: params.row.payment_status === 'unpaid'
					? 'Unpaid'
					: 'Pending Payment';
				const deliveryStatus = params.row.is_delivered ? 'Delivered' : '';

				return (
					<StatusIndicator>
						<StatusItem color={paymentStatus === 'Paid' ? '#16C172' : '#EFB22E'}>
							<StatusDot color={paymentStatus === 'Paid' ? '#16C172' : '#EFB22E'} />
							{paymentStatus}
						</StatusItem>
						{deliveryStatus && (
							<StatusItem color="#16C172">
								<StatusDot color="#16C172" />
								{deliveryStatus}
							</StatusItem>
						)}
					</StatusIndicator>
				);
			},
		},
		{
			field: 'customer_phone',
			headerName: 'Phone',
			minWidth: 160,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-700',
			flex: 2,
		},
		{
			field: 'customer_address',
			headerName: 'Address',
			minWidth: 220,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-700',
			flex: 2,
			isNotSearchable: true, // Often too long and not useful for search
		},
		{
			field: 'date_updated',
			headerName: 'Last Updated',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
	];

	const handleRowClick = (params: { row: OrderInterface }) => {
		const order = params.row;
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Order Row',
			label: `Order ID: ${order.id}`,
		});
		// First update
		setSelectedOrder(order);

		// Use a functional update to access the latest state
		// Note: This is a workaround but doesn't guarantee sequence in all cases
		setSelectedOrder((currentOrder) => {
			// Now that we've updated the order, we can set the panel to visible
			setIsEditPanelVisible(true);
			// Return the same state to avoid changing it
			return currentOrder;
		});
	};

	const showCreateOrderPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Add New Order Button',
			label: 'Add New Order',
		});
		setSelectedOrder({
			customer_name: '',
			customer_email: '',
			customer_phone: '',
			customer_address: '',
			is_order_paid: false,
		} as OrderInterface);
		setIsEditPanelVisible(true);
	};

	const hideOrderPanel = (order?: OrderInterface | null) => {
		setIsEditPanelVisible(false);
		setSelectedOrder(null);

		if (order === null && selectedOrder?.id) {
			// null indicates deletion
			setOrders(orders.filter((o) => o.id !== selectedOrder.id));
			// Refresh storefront orders if it's a storefront order
			if (selectedOrder?.source === 'storefront') {
				storefrontOrdersRef.current?.refreshOrders();
			}
			ReactGA.event({
				category: 'Orders',
				action: 'Deleted Order',
				label: `Order ID: ${selectedOrder.id}`,
			});
		} else if (order) {
			let isOrderNew = true;

			setOrders(
				orders.map((o: OrderInterface) => {
					if (o.id === order.id) {
						isOrderNew = false;
						return order;
					}
					return o;
				})
			);

			if (isOrderNew) {
				setOrders([...orders, order]);
				ReactGA.event({
					category: 'Orders',
					action: 'Created New Order',
					label: `Order ID: ${order.id}`,
				});
			} else {
				// Refresh storefront orders if it's a storefront order
				if (order?.source === 'storefront') {
					storefrontOrdersRef.current?.refreshOrders();
				}
				ReactGA.event({
					category: 'Orders',
					action: 'Updated Order',
					label: `Order ID: ${order.id}`,
				});
			}
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed Order Panel',
				label: 'No Order Changes',
			});
		}
	};

	return (
		<Wrapper>
			<Header
				title="Orders"
				description="Create edit or delete orders here"
				headerComponents={[
					<CreateButton key="create-button" onClick={showCreateOrderPanel}>
						<OrderIcon />
						<span>Add Order</span>
					</CreateButton>,
				]}
			/>
			<Tabbed
				style={{ marginTop: '10px' }}
				sections={[
					{
						label: 'All',
						component: (
							<CustomTable
								columns={columns}
								objects={orders}
								emptyStateComponent={() => (
									<EmptyState
										handleButtonClick={showCreateOrderPanel}
										title="No Orders Yet"
										description="Once you start taking orders from customers, they'll appear here. Track order details, manage statuses, and ensure smooth delivery for every customer."
										buttonText="Add New Order"
										icon={<OrderIcon width={50} height={50} />}
									/>
								)}
								fetchState={fetchState}
								sx={{ marginTop: '0' }}
								onRowClick={(param) => handleRowClick(param)}
								initialRowsPerPage={25}
								sortConfig={{ key: 'date_created', direction: 'desc' }}
							/>
						),
					},
					{
						label: 'Storefront',
						component: (
							<StorefrontOrders
								ref={storefrontOrdersRef}
								onOrderClick={(order) => {
									ReactGA.event({
										category: 'User Interaction',
										action: 'Clicked Storefront Order Row',
										label: `Order ID: ${order.id}`,
									});
									setSelectedOrder(order);
									setIsEditPanelVisible(true);
								}}
							/>
						),
					},
				]}
			/>
			<OrderModal isVisible={isEditPanelVisible} handleHidePanel={hideOrderPanel} order={selectedOrder} />
		</Wrapper>
	);
}
